import { ref, nextTick, shallowRef } from "vue";

export type ScreenshotCapture = {
  blob: Blob;
  file: File;
  objectUrl: string;
  width: number;
  height: number;
};

export type VideoCapture = {
  blob: Blob;
  file: File;
  objectUrl: string;
  /*
   * Whether the recorder stopped itself at the size it was given, rather than
   * being stopped by somebody. Only ever true when startRecording was passed
   * maxBytes, so a caller that never asks for a limit never sees it set.
   */
  reachedLimit?: boolean;
};

export type RecordingOptions = {
  /*
   * Stop on its own once the recording has grown to about this many bytes.
   *
   * For a caller that has to upload the result somewhere with a size limit. A
   * recording that runs past the limit is not a longer recording, it is a
   * failed upload three minutes later, so it is cut here instead. Measured on
   * what the recorder has handed over so far; the last slice arrives after the
   * stop, so pass something comfortably under the real limit.
   */
  maxBytes?: number;
  /*
   * How often the recorder hands over what it has, in milliseconds, while it
   * is being measured. Only used with maxBytes. Without a timeslice the whole
   * recording arrives in one piece at the end, which is too late to measure.
   */
  timeslice?: number;
};

export function useAudioVideoCapture() {
  const sharingState = ref(false);
  const recordingState = ref(false);
  const screenshotingState = ref(false);
  const microphoneState = ref(false);

  const stream = shallowRef<MediaStream | null>(null);
  const micStream = shallowRef<MediaStream | null>(null);
  const recordingStream = shallowRef<MediaStream | null>(null);
  const mediaRecorder = shallowRef<MediaRecorder | null>(null);
  let chunks: Blob[] = [];

  const lastScreenshot = shallowRef<ScreenshotCapture | null>(null);
  const lastVideo = shallowRef<VideoCapture | null>(null);

  function revokeUrl(url?: string) {
    if (url) URL.revokeObjectURL(url);
  }

  function stopTracks(target: MediaStream | null | undefined) {
    target?.getTracks().forEach((t) => t.stop());
  }

  function cleanupMicStream() {
    stopTracks(micStream.value);
    micStream.value = null;
    microphoneState.value = false;
  }

  function cleanupRecordingStream() {
    // wrapper stream only; do not stop tracks here
    // because its video track is the same shared screen track
    recordingStream.value = null;
  }

  function cleanupStream() {
    stopTracks(stream.value);
    stream.value = null;
    sharingState.value = false;
  }

  function finalizeRecording() {
    if (!mediaRecorder.value || mediaRecorder.value.state === "inactive") {
      return;
    }
    mediaRecorder.value.stop();
  }

  function clearLastScreenshot() {
    revokeUrl(lastScreenshot.value?.objectUrl);
    lastScreenshot.value = null;
  }

  function clearLastVideo() {
    revokeUrl(lastVideo.value?.objectUrl);
    lastVideo.value = null;
  }

  async function requestShare() {
    if (stream.value) return;

    const s = await navigator.mediaDevices.getDisplayMedia({
      video: {
        frameRate: { ideal: 10, max: 12 },
        displaySurface: "monitor",
      },
      audio: false,
      // @ts-expect-error browser-specific hint
      selfBrowserSurface: "include",
      // preferCurrentTab: true,
    });

    stream.value = s;
    sharingState.value = true;

    const track = s.getVideoTracks()[0];
    track.onended = () => {
      if (mediaRecorder.value && mediaRecorder.value.state !== "inactive") {
        mediaRecorder.value.stop();
      } else {
        cleanupMicStream();
        cleanupRecordingStream();
      }

      cleanupStream();
    };
  }

  async function requestMicrophoneSilently(): Promise<MediaStream | null> {
    if (micStream.value) return micStream.value;

    try {
      const s = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
        video: false,
      });

      micStream.value = s;
      microphoneState.value = s
        .getAudioTracks()
        .some((t) => t.readyState === "live");

      const audioTrack = s.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.onended = () => {
          cleanupMicStream();
        };
      }

      return s;
    } catch {
      microphoneState.value = false;
      return null;
    }
  }

  async function takeScreenshot(): Promise<ScreenshotCapture> {
    screenshotingState.value = true;

    try {
      await nextTick();
      await new Promise<void>((resolve) => {
        requestAnimationFrame(() => resolve());
      });

      if (!stream.value) {
        await requestShare();
      }

      const activeStream = stream.value;
      if (!activeStream) {
        throw new Error("No active capture stream");
      }

      const video = document.createElement("video");
      video.srcObject = activeStream;
      video.muted = true;
      video.playsInline = true;

      await video.play();

      await new Promise<void>((resolve) => {
        if (
          video.readyState >= 2 &&
          video.videoWidth > 0 &&
          video.videoHeight > 0
        ) {
          resolve();
          return;
        }

        const onLoaded = () => {
          video.removeEventListener("loadeddata", onLoaded);
          resolve();
        };

        video.addEventListener("loadeddata", onLoaded);
      });

      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      const ctx = canvas.getContext("2d");
      if (!ctx) {
        video.pause();
        video.srcObject = null;
        throw new Error("Could not get canvas context");
      }

      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob((b) => {
          if (!b) {
            reject(new Error("Failed to create screenshot blob"));
            return;
          }
          resolve(b);
        }, "image/png");
      });

      video.pause();
      video.srcObject = null;

      clearLastScreenshot();

      const objectUrl = URL.createObjectURL(blob);
      const file = new File([blob], "screenshot.png", { type: "image/png" });

      const result: ScreenshotCapture = {
        blob,
        file,
        objectUrl,
        width: canvas.width,
        height: canvas.height,
      };

      screenshotingState.value = false;
      lastScreenshot.value = result;
      return result;
    } catch (error) {
      screenshotingState.value = false;
      throw error;
    }
  }

  async function startRecording(options: RecordingOptions = {}) {
    if (recordingState.value) return;

    const maxBytes =
      typeof options.maxBytes === "number" && options.maxBytes > 0
        ? options.maxBytes
        : null;

    if (!stream.value) {
      await requestShare();
    }

    const activeStream = stream.value;
    if (!activeStream) {
      throw new Error("No active capture stream");
    }

    const mic = await requestMicrophoneSilently();

    const combinedTracks: MediaStreamTrack[] = [
      ...activeStream.getVideoTracks(),
      ...(mic ? mic.getAudioTracks() : []),
    ];

    const finalRecordingStream = new MediaStream(combinedTracks);
    recordingStream.value = finalRecordingStream;

    chunks = [];

    /*
     * WebM wherever the browser records it, and mp4 only as a last resort.
     *
     * The mp4 step is for Safari before 18.4, which records mp4 and nothing
     * else. Asking it for webm threw, so recording there never started at all;
     * everywhere that already recorded webm picks exactly what it did before.
     * With none of them supported the browser is left to choose, which is
     * better than a type it has just said it cannot do.
     */
    const mimeType = [
      "video/webm;codecs=vp9",
      "video/webm;codecs=vp8",
      "video/webm",
      "video/mp4",
    ].find((candidate) => MediaRecorder.isTypeSupported(candidate));

    const recorder = new MediaRecorder(finalRecordingStream, {
      ...(mimeType ? { mimeType } : {}),
      videoBitsPerSecond: 800 * 1000,
      audioBitsPerSecond: 64 * 1000,
    });
    mediaRecorder.value = recorder;

    // Counted per recording, so one that was cut short cannot mark the next.
    let recordedBytes = 0;
    let reachedLimit = false;

    recorder.ondataavailable = (e: BlobEvent) => {
      if (e.data.size > 0) chunks.push(e.data);

      /*
       * Measured only while it is still recording. The last slice arrives after
       * a stop, from the stop control or the browser's own bar, and the
       * recorder is already inactive by then. Counting it could tip a recording
       * somebody stopped themselves over the line, and it would be reported as
       * having stopped itself.
       */
      if (maxBytes === null || reachedLimit || recorder.state !== "recording")
        return;

      recordedBytes += e.data.size;

      if (recordedBytes >= maxBytes) {
        reachedLimit = true;
        // Stopped the ordinary way, so onstop still builds the file and
        // whoever is waiting on lastVideo still gets it.
        finalizeRecording();
      }
    };

    recorder.onstop = () => {
      const type = recorder.mimeType || "video/webm";
      const blob = new Blob(chunks, { type });

      clearLastVideo();

      const objectUrl = URL.createObjectURL(blob);
      const file = new File(
        [blob],
        type.includes("mp4") ? "recording.mp4" : "recording.webm",
        { type },
      );

      lastVideo.value = {
        blob,
        file,
        objectUrl,
        reachedLimit,
      };

      chunks = [];
      recordingState.value = false;
      mediaRecorder.value = null;

      cleanupMicStream();
      cleanupRecordingStream();
    };

    /*
     * Sliced only when there is a limit to watch. Without one the recorder is
     * started exactly as it always was, in one piece, so a caller that never
     * asked for a limit gets the same file it always got.
     */
    if (maxBytes !== null) {
      recorder.start(options.timeslice ?? 1000);
    } else {
      recorder.start();
    }
    recordingState.value = true;
  }

  function stopRecording(): Promise<VideoCapture | null> {
    if (!mediaRecorder.value || mediaRecorder.value.state === "inactive") {
      cleanupMicStream();
      cleanupRecordingStream();
      recordingState.value = false;
      mediaRecorder.value = null;
      return Promise.resolve(lastVideo.value);
    }

    return new Promise((resolve) => {
      const recorder = mediaRecorder.value as MediaRecorder;

      const handler = () => {
        recorder.removeEventListener("stop", handler);
        resolve(lastVideo.value);
      };

      recorder.addEventListener("stop", handler);

      recorder.stop();
      // mic cleanup happens in onstop, after recorder fully stops
    });
  }

  function stopSharing() {
    if (mediaRecorder.value && mediaRecorder.value.state !== "inactive") {
      mediaRecorder.value.stop();
    } else {
      cleanupMicStream();
      cleanupRecordingStream();
    }

    cleanupStream();
  }

  function dispose() {
    if (mediaRecorder.value && mediaRecorder.value.state !== "inactive") {
      mediaRecorder.value.stop();
    }

    cleanupMicStream();
    cleanupRecordingStream();
    cleanupStream();

    clearLastScreenshot();
    clearLastVideo();

    mediaRecorder.value = null;
    chunks = [];

    recordingState.value = false;
    screenshotingState.value = false;
    microphoneState.value = false;
  }

  return {
    sharingState,
    recordingState,
    screenshotingState,
    microphoneState,
    lastScreenshot,
    lastVideo,
    requestShare,
    takeScreenshot,
    startRecording,
    stopRecording,
    stopSharing,
    clearLastScreenshot,
    clearLastVideo,
    dispose,
  };
}
